import { supabase } from '@/lib/supabase';
import { getTenantId } from '@/repos/contentRepo';
import { convertFrontendToDB, convertDBToFrontend } from './calendarTypeMapping';

export interface CalendarEvent {
  id?: string;
  user_id: string;
  tenant_id?: string;
  title: string;
  type: 'appointment' | 'available' | 'blocked' | 'event';
  start_time: string;
  end_time: string;
  status: 'confirmed' | 'pending' | 'cancelled' | 'busy' | 'available';
  description?: string;
  attendees: string[];
  visibility: 'public' | 'private' | 'restricted';
  created_at?: string;
  updated_at?: string;
}

// Interface para dados do frontend
export interface FrontendCalendarEvent {
  id?: string;
  user_id?: string;
  title: string;
  type: 'atendimento' | 'disponivel' | 'bloqueado' | 'evento';
  startTime: string;
  endTime: string;
  status?: 'confirmed' | 'pending' | 'cancelled' | 'busy' | 'available';
  description?: string;
  attendees: string[];
  visibility: 'public' | 'private' | 'restricted';
  createdAt?: string;
  updatedAt?: string;
}

export async function createCalendarEvent(eventData: any): Promise<CalendarEvent> {
  try {
    const tenantId = await getTenantId();
    
    console.log('🔍 createCalendarEvent: dados recebidos do frontend:', eventData);
    
    // Converter dados do frontend para formato do banco
    const dbEventData = convertFrontendToDB(eventData);
    
    console.log('🔍 createCalendarEvent: dados convertidos para banco:', dbEventData);
    console.log('🔍 createCalendarEvent: verificação final de formato:', {
      start_time: dbEventData.start_time,
      end_time: dbEventData.end_time,
      start_time_type: typeof dbEventData.start_time,
      end_time_type: typeof dbEventData.end_time
    });
    
    const { data, error } = await supabase
      .from('calendar_events')
      .insert([{
        ...dbEventData,
        tenant_id: tenantId
      }])
      .select()
      .single();

    if (error) throw error;
    
    console.log('🔍 createCalendarEvent: evento criado no banco:', data);
    return data as CalendarEvent;
  } catch (error) {
    console.error('❌ Erro ao criar evento de calendário:', error);
    throw error;
  }
}

export async function updateCalendarEvent(id: string, eventData: Partial<CalendarEvent>): Promise<any> {
  try {
    // Converter dados do frontend para formato do banco
    const dbEventData = convertFrontendToDB(eventData);
    
    console.log('🔍 updateCalendarEvent: dados convertidos para banco:', dbEventData);
    
    const { data, error } = await supabase
      .from('calendar_events')
      .update({
        ...dbEventData,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    
    // Converter dados do banco de volta para formato do frontend
    const frontendData = convertDBToFrontend(data);
    
    console.log('🔍 updateCalendarEvent: dados convertidos para frontend:', frontendData);
    console.log('🔍 updateCalendarEvent: mapeamento de tipo:', {
      original: data.type,
      convertido: frontendData.type
    });
    
    return frontendData;
  } catch (error) {
    console.error('❌ Erro ao atualizar evento de calendário:', error);
    throw error;
  }
}

export async function deleteCalendarEvent(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('calendar_events')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('❌ Erro ao deletar evento de calendário:', error);
    throw error;
  }
}

export async function getCalendarEvents(userId: string, tenantId?: string): Promise<any[]> {
  try {
    const currentTenantId = tenantId || await getTenantId();
    
    console.log('🔍 getCalendarEvents: buscando eventos para userId:', userId, 'tenantId:', currentTenantId);
    
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('user_id', userId)
      .eq('tenant_id', currentTenantId)
      .order('start_time', { ascending: true });

    if (error) throw error;
    
    console.log('🔍 getCalendarEvents: dados brutos do banco:', data);
    
    // Converter dados do banco para formato do frontend
    const convertedData = (data || []).map(convertDBToFrontend);
    
    console.log('🔍 getCalendarEvents: dados convertidos para frontend:', convertedData);
    
    return convertedData;
  } catch (error) {
    console.error('❌ Erro ao buscar eventos de calendário:', error);
    throw error;
  }
}

export async function getPublicCalendarEvents(userId: string, tenantId?: string): Promise<any[]> {
  try {
    const currentTenantId = tenantId || await getTenantId();
    
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('user_id', userId)
      .eq('tenant_id', currentTenantId)
      .eq('visibility', 'public')
      .order('start_time', { ascending: true });

    if (error) throw error;
    
    // Converter dados do banco para formato do frontend
    return (data || []).map(convertDBToFrontend);
  } catch (error) {
    console.error('❌ Erro ao buscar eventos públicos de calendário:', error);
    throw error;
  }
}

export async function getCalendarEventById(id: string): Promise<CalendarEvent | null> {
  try {
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as CalendarEvent;
  } catch (error) {
    console.error('❌ Erro ao buscar evento por ID:', error);
    return null;
  }
}
